import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { IconClock } from "@tabler/icons-react";

type TableCellContentTimeProps = {
  onFocusAction?: () => void;
  border?: string;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  iconSize?: number;
  viewOnly?: boolean;
  interval?: number;
};

// Which segment is active
type Segment = "hour" | "minute";

export const TableCellContentTime: React.FC<TableCellContentTimeProps> = ({
  border = "outline-tertiary focus-within:border focus-within:outline-focus-bam border focus-within:outline-hidden",
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height = "h-9",
  surface = "surface-primary",
  fontSize = "body-2-regular",
  padding = "px-2 py-1",
  rounded = "rounded-[10px]",
  viewOnly,
  interval = 15,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLSpanElement>(null);
  const minuteRef = useRef<HTMLSpanElement>(null);
  const [activeSegment, setActiveSegment] = useState<Segment | null>(null);
  // Buffer for multi-digit typing within a segment
  const typingBuffer = useRef("");
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parse value into hour/minute
  const parsed = useMemo(() => {
    if (!value) return { hour: -1, minute: -1 };
    const match = value.match(/(\d{2}):(\d{2})/);
    if (!match) return { hour: -1, minute: -1 };
    return { hour: parseInt(match[1], 10), minute: parseInt(match[2], 10) };
  }, [value]);

  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);

  // Sync from prop
  useEffect(() => {
    setHour(parsed.hour);
    setMinute(parsed.minute);
  }, [parsed.hour, parsed.minute]);

  // Valid minute steps for the interval
  const minuteSteps = useMemo(() => {
    const steps: number[] = [];
    for (let m = 0; m < 60; m += interval) {
      steps.push(m);
    }
    return steps;
  }, [interval]);

  // Snap a minute value to the nearest valid step
  const snapMinute = useCallback(
    (m: number) => {
      if (minuteSteps.includes(m)) return m;
      // Find nearest
      let best = minuteSteps[0];
      let bestDist = Math.abs(m - best);
      for (const step of minuteSteps) {
        const dist = Math.abs(m - step);
        if (dist < bestDist) {
          best = step;
          bestDist = dist;
        }
      }
      return best;
    },
    [minuteSteps],
  );

  // Emit the combined value
  const emitValue = useCallback(
    (h: number, m: number) => {
      if (h < 0 || m < 0) return;
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      onChangeValue(`${hh}:${mm}`);
    },
    [onChangeValue],
  );

  // Format display strings
  const hourDisplay = hour >= 0 ? String(hour).padStart(2, "0") : "--";
  const minuteDisplay = minute >= 0 ? String(minute).padStart(2, "0") : "--";

  // Select text in a segment span (visual highlight)
  const selectSegment = useCallback((seg: Segment) => {
    const ref = seg === "hour" ? hourRef : minuteRef;
    if (!ref.current) return;
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  // Focus a segment
  const focusSegment = useCallback(
    (seg: Segment) => {
      setActiveSegment(seg);
      typingBuffer.current = "";
      const ref = seg === "hour" ? hourRef : minuteRef;
      ref.current?.focus();
      // Defer selection to after focus
      requestAnimationFrame(() => selectSegment(seg));
      if (onFocusAction) onFocusAction();
    },
    [selectSegment, onFocusAction],
  );

  // Clear typing timeout
  const clearTypingTimeout = useCallback(() => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
      typingTimeout.current = null;
    }
  }, []);

  // Get next/prev minute step
  const getNextMinute = useCallback(
    (current: number, direction: 1 | -1) => {
      const idx = minuteSteps.indexOf(current);
      if (idx === -1) {
        // Current isn't a valid step, snap first
        return snapMinute(current);
      }
      const nextIdx = idx + direction;
      if (nextIdx < 0) return minuteSteps[minuteSteps.length - 1];
      if (nextIdx >= minuteSteps.length) return minuteSteps[0];
      return minuteSteps[nextIdx];
    },
    [minuteSteps, snapMinute],
  );

  // Handle arrow keys for increment/decrement
  const handleArrow = useCallback(
    (seg: Segment, direction: 1 | -1) => {
      if (seg === "hour") {
        const current = hour >= 0 ? hour : 0;
        const next = (current + direction + 24) % 24;
        setHour(next);
        const m = minute >= 0 ? minute : 0;
        setMinute(m);
        emitValue(next, m);
      } else {
        const h = hour >= 0 ? hour : 0;
        setHour(h);
        const current = minute >= 0 ? minute : 0;
        const next = getNextMinute(current, direction);
        setMinute(next);
        emitValue(h, next);
      }
    },
    [hour, minute, emitValue, getNextMinute],
  );

  // Handle digit typing
  const handleDigit = useCallback(
    (seg: Segment, digit: string) => {
      clearTypingTimeout();
      const buffer = typingBuffer.current + digit;

      if (seg === "hour") {
        const num = parseInt(buffer, 10);
        if (buffer.length >= 2) {
          // Two digits typed — clamp and commit
          const clamped = Math.min(num, 23);
          setHour(clamped);
          typingBuffer.current = "";
          const m = minute >= 0 ? minute : 0;
          setMinute(m);
          emitValue(clamped, m);
          // Auto-advance to minute
          requestAnimationFrame(() => focusSegment("minute"));
        } else {
          // First digit — set tentatively, wait for second
          typingBuffer.current = buffer;
          if (num > 2) {
            // Can't form a valid hour >23, commit immediately
            const clamped = Math.min(num, 23);
            setHour(clamped);
            typingBuffer.current = "";
            const m = minute >= 0 ? minute : 0;
            setMinute(m);
            emitValue(clamped, m);
            requestAnimationFrame(() => focusSegment("minute"));
          } else {
            setHour(num);
            // Wait for second digit
            typingTimeout.current = setTimeout(() => {
              typingBuffer.current = "";
              const m = minute >= 0 ? minute : 0;
              setMinute(m);
              emitValue(num, m);
              requestAnimationFrame(() => focusSegment("minute"));
            }, 800);
          }
        }
      } else {
        // Minute segment
        const num = parseInt(buffer, 10);
        if (buffer.length >= 2) {
          const clamped = Math.min(num, 59);
          const snapped = snapMinute(clamped);
          setMinute(snapped);
          typingBuffer.current = "";
          const h = hour >= 0 ? hour : 0;
          setHour(h);
          emitValue(h, snapped);
          // Blur after full minute entry
          requestAnimationFrame(() => minuteRef.current?.blur());
        } else {
          typingBuffer.current = buffer;
          if (num > 5) {
            const snapped = snapMinute(Math.min(num, 59));
            setMinute(snapped);
            typingBuffer.current = "";
            const h = hour >= 0 ? hour : 0;
            setHour(h);
            emitValue(h, snapped);
            requestAnimationFrame(() => minuteRef.current?.blur());
          } else {
            // Show the digit as-is, wait for second
            setMinute(num * 10); // tentative display e.g. "30" for "3"
            typingTimeout.current = setTimeout(() => {
              const snapped = snapMinute(num * 10);
              setMinute(snapped);
              typingBuffer.current = "";
              const h = hour >= 0 ? hour : 0;
              setHour(h);
              emitValue(h, snapped);
            }, 800);
          }
        }
      }
    },
    [
      hour,
      minute,
      emitValue,
      snapMinute,
      focusSegment,
      clearTypingTimeout,
    ],
  );

  // Keyboard handler
  const handleKeyDown = useCallback(
    (seg: Segment, e: React.KeyboardEvent) => {
      if (viewOnly) return;

      // Digits
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        handleDigit(seg, e.key);
        return;
      }

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          clearTypingTimeout();
          typingBuffer.current = "";
          handleArrow(seg, 1);
          requestAnimationFrame(() => selectSegment(seg));
          break;
        case "ArrowDown":
          e.preventDefault();
          clearTypingTimeout();
          typingBuffer.current = "";
          handleArrow(seg, -1);
          requestAnimationFrame(() => selectSegment(seg));
          break;
        case "ArrowRight":
        case "Tab":
          if (seg === "hour" && (!e.shiftKey || e.key === "ArrowRight")) {
            e.preventDefault();
            clearTypingTimeout();
            typingBuffer.current = "";
            focusSegment("minute");
          }
          break;
        case "ArrowLeft":
          if (seg === "minute") {
            e.preventDefault();
            clearTypingTimeout();
            typingBuffer.current = "";
            focusSegment("hour");
          }
          break;
        case "Backspace":
        case "Delete":
          e.preventDefault();
          clearTypingTimeout();
          typingBuffer.current = "";
          if (seg === "hour") {
            setHour(-1);
          } else {
            setMinute(-1);
          }
          break;
        case "Escape":
          e.preventDefault();
          // This field CONSUMES Escape (deselect segment + blur), so letting it
          // bubble would additionally close a surrounding popup on a key the
          // user aimed at the time field.
          e.stopPropagation();
          clearTypingTimeout();
          typingBuffer.current = "";
          setActiveSegment(null);
          containerRef.current?.blur();
          (seg === "hour" ? hourRef : minuteRef).current?.blur();
          break;
      }
    },
    [
      viewOnly,
      handleDigit,
      handleArrow,
      selectSegment,
      focusSegment,
      clearTypingTimeout,
    ],
  );

  // Handle blur on a segment
  const handleSegmentBlur = useCallback(() => {
    clearTypingTimeout();
    typingBuffer.current = "";
    // Check if focus moved to the other segment (defer to let focus settle)
    requestAnimationFrame(() => {
      if (
        document.activeElement !== hourRef.current &&
        document.activeElement !== minuteRef.current
      ) {
        setActiveSegment(null);
      }
    });
  }, [clearTypingTimeout]);

  // Segment style classes
  const segmentClass = (seg: Segment) =>
    `outline-none cursor-default select-all rounded-sm px-0.5 tabular-nums ${
      activeSegment === seg ? "surface-tertiary" : ""
    }`;

  return (
    <>
      {isEditing && isSelected ? (
        <div className="flex flex-col flex-1">
          <div
            ref={containerRef}
            className={`flex items-center gap-0 w-full ${height} ${border} ${rounded} ${surface} ${padding} outline-quaternary ${
              viewOnly ? "opacity-50 cursor-not-allowed" : "cursor-text"
            }`}
            onClick={() => {
              if (!viewOnly && !activeSegment) focusSegment("hour");
            }}
          >
            <IconClock
              size={14}
              className="font-tertiary mr-1.5 shrink-0"
            />
            <span
              ref={hourRef}
              role="spinbutton"
              aria-label="Hours"
              aria-valuemin={0}
              aria-valuemax={23}
              aria-valuenow={hour >= 0 ? hour : undefined}
              tabIndex={viewOnly ? -1 : 0}
              className={`${fontSize} ${segmentClass("hour")}`}
              onFocus={() => {
                if (!viewOnly) {
                  setActiveSegment("hour");
                  typingBuffer.current = "";
                  requestAnimationFrame(() => selectSegment("hour"));
                  if (onFocusAction) onFocusAction();
                }
              }}
              onBlur={handleSegmentBlur}
              onKeyDown={(e) => handleKeyDown("hour", e)}
              onMouseUp={(e) => {
                // Prevent default cursor placement, keep full selection
                e.preventDefault();
                if (!viewOnly) focusSegment("hour");
              }}
            >
              {hourDisplay}
            </span>
            <span className={`${fontSize} font-tertiary`}>:</span>
            <span
              ref={minuteRef}
              role="spinbutton"
              aria-label="Minutes"
              aria-valuemin={0}
              aria-valuemax={59}
              aria-valuenow={minute >= 0 ? minute : undefined}
              tabIndex={viewOnly ? -1 : 0}
              className={`${fontSize} ${segmentClass("minute")}`}
              onFocus={() => {
                if (!viewOnly) {
                  setActiveSegment("minute");
                  typingBuffer.current = "";
                  requestAnimationFrame(() => selectSegment("minute"));
                  if (onFocusAction) onFocusAction();
                }
              }}
              onBlur={handleSegmentBlur}
              onKeyDown={(e) => handleKeyDown("minute", e)}
              onMouseUp={(e) => {
                e.preventDefault();
                if (!viewOnly) focusSegment("minute");
              }}
            >
              {minuteDisplay}
            </span>
          </div>
          {error && <p className="mt-1 body-3-regular opacity-0">{error}</p>}
        </div>
      ) : hour >= 0 && minute >= 0 ? (
        <div className="flex items-center gap-1.5 font-secondary">
          <IconClock size={14} className="font-tertiary" />
          <span>{`${hourDisplay}:${minuteDisplay}`}</span>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
